import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ArrowLeft, Coffee, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtHoras } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const COLORES = ["#f59e0b", "#8b5cf6", "#06b6d4", "#ec4899", "#10b981", "#6366f1", "#ef4444"];

function KpiCard({ label, value, sub, variacion }) {
  const up = variacion != null && variacion > 0;
  const down = variacion != null && variacion < 0;
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="font-display mt-1 text-2xl font-light text-zinc-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
      {variacion != null && (
        <p className={`mt-2 flex items-center gap-1 text-xs font-medium ${up ? "text-rose-600" : down ? "text-emerald-600" : "text-zinc-500"}`}>
          {up ? <TrendingUp className="h-3.5 w-3.5" /> : down ? <TrendingDown className="h-3.5 w-3.5" /> : null}
          {variacion > 0 ? "+" : ""}
          {variacion}% vs año comparado
        </p>
      )}
    </div>
  );
}

function TablaSimple({ columns, rows, empty }) {
  if (!rows?.length) return <p className="py-6 text-center text-sm text-zinc-400">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-100 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            {columns.map((c) => (
              <th key={c.key} className="px-3 py-2 whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id ?? i} className="border-b border-zinc-50 hover:bg-zinc-50/80">
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2 text-zinc-700 whitespace-nowrap">
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AnalisisPausas() {
  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [anioComp, setAnioComp] = useState(anioActual - 1);
  const [centroId, setCentroId] = useState("");
  const [depId, setDepId] = useState("");
  const [data, setData] = useState(null);
  const [centros, setCentros] = useState([]);
  const [deps, setDeps] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/centros").then((r) => setCentros(r.data)).catch(() => {}),
      api.get("/departamentos").then((r) => setDeps(r.data)).catch(() => {}),
    ]);
  }, []);

  const depsFiltrados = useMemo(
    () => (centroId ? deps.filter((d) => String(d.centro_id) === centroId) : deps),
    [deps, centroId]
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = { anio, anio_comparacion: anioComp || undefined };
      if (depId) params.departamento_id = Number(depId);
      if (centroId) params.centro_id = Number(centroId);
      const { data: res } = await api.get("/analitica-pausas", { params });
      setData(res);
      if (res.anios_disponibles?.length && !res.anios_disponibles.includes(anio)) {
        setAnio(res.anios_disponibles[res.anios_disponibles.length - 1]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [anio, anioComp, depId, centroId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const actual = data?.actual;
  const res = actual?.resumen;
  const varr = data?.variaciones;
  const anios = data?.anios_disponibles || [anioActual];

  return (
    <div className="space-y-6" data-testid="analisis-pausas-page">
      <PageHeader
        icon={Coffee}
        title="Análisis de pausas"
        subtitle="Tiempo en pausa por año, departamento y centro · comparativa interanual"
        sectionKey="informe-analisis_pausas"
        brandGradient={false}
      >
        <Button asChild variant="secondary" size="sm" className="gap-1.5 bg-white/20 text-white hover:bg-white/30 border-0">
          <Link to="/admin">
            <ArrowLeft className="h-4 w-4" />
            Panel
          </Link>
        </Button>
      </PageHeader>

      <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-4 py-3 text-sm text-amber-950">
        Identifica en qué momentos y áreas se pierde más tiempo laborable en pausas. Usa la comparación entre años
        para medir si las medidas adoptadas reducen el impacto.
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
        <div className="space-y-1">
          <label className="text-xs font-medium text-zinc-600">Año</label>
          <select className="h-9 rounded-md border border-zinc-200 px-2 text-sm" value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
            {anios.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-zinc-600">Comparar con</label>
          <select
            className="h-9 rounded-md border border-zinc-200 px-2 text-sm"
            value={anioComp ?? ""}
            onChange={(e) => setAnioComp(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Sin comparar</option>
            {anios.filter((y) => y !== anio).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-zinc-600">Centro</label>
          <select className="h-9 min-w-[140px] rounded-md border border-zinc-200 px-2 text-sm" value={centroId} onChange={(e) => { setCentroId(e.target.value); setDepId(""); }}>
            <option value="">Todos</option>
            {centros.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-zinc-600">Departamento</label>
          <select className="h-9 min-w-[160px] rounded-md border border-zinc-200 px-2 text-sm" value={depId} onChange={(e) => setDepId(e.target.value)}>
            <option value="">Todos</option>
            {depsFiltrados.map((d) => (
              <option key={d.id} value={d.id}>{d.nombre}</option>
            ))}
          </select>
        </div>
        {data?.periodo && (
          <Badge variant="outline" className="ml-auto">
            Periodo: {data.periodo.desde} → {data.periodo.hasta}
          </Badge>
        )}
      </div>

      {cargando ? (
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-950" />
        </div>
      ) : !res ? (
        <p className="text-center text-zinc-500">Sin datos de pausas en el periodo seleccionado.</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard label="Tiempo total en pausa" value={`${res.total_horas} h`} sub={`${res.total_minutos} min · ${res.total_pausas} pausas`} variacion={varr?.total_minutos} />
            <KpiCard label="Media por jornada" value={`${res.media_minutos_por_jornada} min`} sub={`${res.media_pausas_por_jornada} pausas/jornada`} variacion={varr?.media_minutos_por_jornada} />
            <KpiCard label="Jornadas analizadas" value={res.jornadas_analizadas} sub={`${res.pct_jornadas_con_pausa}% con al menos una pausa`} />
            <KpiCard label="Trabajadores" value={res.trabajadores} sub={`${res.jornadas_con_pausa} jornadas con pausa`} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <h3 className="font-display text-base font-medium text-zinc-900">Evolución mensual (minutos)</h3>
              <p className="text-xs text-zinc-500">Comparativa {anio} vs {anioComp || "—"}</p>
              <div className="mt-3 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.comparativa_mensual || actual.por_mes}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="mes_label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="minutos_actual" name={`${anio}`} fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    {data.comparativa_mensual && (
                      <Line type="monotone" dataKey="minutos_comparacion" name={`${anioComp}`} stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <h3 className="font-display text-base font-medium text-zinc-900">Distribución por motivo</h3>
              <div className="mt-3 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={actual.por_motivo} dataKey="minutos" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={({ label, pct_minutos }) => `${label} ${pct_minutos}%`}>
                      {actual.por_motivo.map((_, i) => (
                        <Cell key={i} fill={COLORES[i % COLORES.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v} min`, "Tiempo"]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <h3 className="font-display text-base font-medium text-zinc-900">Horas del día con más pausas (año)</h3>
              <div className="mt-3 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={actual.por_hora}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="hora" tick={{ fontSize: 10 }} interval={1} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="inicios" name="Inicios" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <h3 className="font-display text-base font-medium text-zinc-900">Por día de la semana</h3>
              <div className="mt-3 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={actual.por_dia_semana}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="dia" />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="minutos" name="Minutos" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-zinc-100 px-4 py-3">
              <h3 className="font-display text-base font-medium text-zinc-900">Por departamento</h3>
              <p className="text-xs text-zinc-500">Pérdida media de tiempo por jornada laboral</p>
            </div>
            <TablaSimple
              empty="Sin pausas por departamento"
              rows={actual.por_departamento}
              columns={[
                { key: "nombre", label: "Departamento" },
                { key: "centro_nombre", label: "Centro" },
                { key: "pausas", label: "Pausas" },
                { key: "minutos", label: "Minutos", render: (r) => `${r.minutos} min (${fmtHoras(Math.round(r.minutos * 60))})` },
                { key: "jornadas", label: "Jornadas" },
                { key: "media_minutos_jornada", label: "Media/jornada", render: (r) => `${r.media_minutos_jornada} min` },
                { key: "media_pausas_jornada", label: "Pausas/jornada" },
              ]}
            />
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-zinc-100 px-4 py-3">
              <h3 className="font-display text-base font-medium text-zinc-900">Por centro de trabajo</h3>
            </div>
            <TablaSimple
              empty="Sin datos por centro"
              rows={actual.por_centro}
              columns={[
                { key: "nombre", label: "Centro" },
                { key: "pausas", label: "Pausas" },
                { key: "minutos", label: "Minutos totales" },
                { key: "jornadas", label: "Jornadas" },
                { key: "media_minutos_jornada", label: "Media/jornada", render: (r) => `${r.media_minutos_jornada} min` },
              ]}
            />
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-zinc-100 px-4 py-3">
              <h3 className="font-display text-base font-medium text-zinc-900">Trabajadores con más tiempo en pausa</h3>
              <p className="text-xs text-zinc-500">Top 25 del periodo · útil para detectar patrones individuales</p>
            </div>
            <TablaSimple
              empty="Sin pausas registradas"
              rows={actual.top_trabajadores}
              columns={[
                { key: "nombre", label: "Empleado" },
                { key: "departamento", label: "Departamento" },
                { key: "pausas", label: "Pausas" },
                { key: "minutos", label: "Minutos totales" },
                { key: "jornadas", label: "Jornadas" },
                { key: "media_minutos_jornada", label: "Media/jornada", render: (r) => `${r.media_minutos_jornada} min` },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}
